/**
 * Tela de Estoque — tabela, como no sistema atual.
 *
 * Erro que motivou esta tela: a primeira versão era uma grade de fotos com
 * busca e categorias, que é a cara da vitrine de Pedidos. O Lucas abriu o
 * Escritório e disse, com razão, que tinha caído no módulo de compras.
 *
 * A distinção é real e vale respeitar: quem abre o Estoque quer **comparar
 * números** e ver o que está vencendo — colunas alinhadas, tudo na mesma tela.
 * Quem abre a vitrine quer **reconhecer o material pela imagem**. São dois
 * trabalhos diferentes e pedem duas telas diferentes.
 *
 * Colunas iguais às de hoje, mais uma: "Disponível". Ela existe porque agora o
 * estoque tem três números, e é o disponível que se pode prometer a alguém.
 */

import { api } from '../api.js';
import { escapar } from '../comum/formato.js';
import { montarExtrato } from './extrato.js';

interface LinhaEstoque {
  codigo: string;
  nome: string;
  unidade: string | null;
  categoria: string | null;
  fisico: number;
  comprometido: number;
  disponivel: number;
  ultimaEntrada: string | null;
  ultimaSaida: string | null;
  validade: string | null;
  situacao: 'sem_controle' | 'sem_registro' | 'vencido' | 'vencendo' | 'atencao' | 'normal';
}

interface Resposta {
  total: number;
  pagina: number;
  paginas: number;
  itens: LinhaEstoque[];
}

interface Resumo {
  itens: number;
  negativos: number;
  zerados: number;
  comprometidos: number;
  vencidos: number;
  vencendo: number;
}

const SITUACAO: Record<LinhaEstoque['situacao'], { texto: string; classe: string }> = {
  sem_controle: { texto: '—', classe: 'v-neutro' },
  sem_registro: { texto: 'sem registro', classe: 'v-atencao' },
  vencido: { texto: 'vencido', classe: 'v-vencido' },
  vencendo: { texto: 'vencendo', classe: 'v-vencendo' },
  atencao: { texto: 'atenção', classe: 'v-atencao' },
  normal: { texto: 'normal', classe: 'v-normal' },
};

export function montarEstoque(raiz: HTMLElement): void {
  const estado = { busca: '', filtro: 'todos', ordem: 'codigo', direcao: 'asc', pagina: 1 };
  let ultima = 0;

  raiz.innerHTML = `
    <div class="kpis" id="kpis"></div>
    <div class="barra-busca">
      <input class="campo" id="busca" type="search" placeholder="Buscar por código, nome ou categoria…"
             autocomplete="off" spellcheck="false">
      <select class="campo campo-curto" id="filtro">
        <option value="todos">Todos os itens</option>
        <option value="com_saldo">Com saldo</option>
        <option value="negativo">Saldo negativo</option>
        <option value="vencido">Vencidos</option>
        <option value="vencendo">Vencendo (7 dias)</option>
        <option value="atencao">Atenção (30 dias)</option>
      </select>
      <select class="campo campo-curto" id="ordem">
        <option value="codigo">Código</option>
        <option value="nome">Nome</option>
        <option value="saldo_desc">Maior saldo</option>
        <option value="saldo">Menor saldo</option>
        <option value="ultima_entrada">Última entrada</option>
        <option value="ultima_saida">Última saída</option>
      </select>
    </div>
    <p class="dica">Para corrigir um saldo que não bate com a contagem física, use <b>⚖️ Corrigir</b>.
       A correção não apaga nada: entra como lançamento de ajuste, com justificativa, e fica no histórico do item.</p>
    <div id="tabela"><p class="carregando">Carregando…</p></div>
    <div class="paginacao" id="paginacao"></div>`;

  const campo = raiz.querySelector<HTMLInputElement>('#busca')!;
  const filtro = raiz.querySelector<HTMLSelectElement>('#filtro')!;
  const ordem = raiz.querySelector<HTMLSelectElement>('#ordem')!;
  const tabela = raiz.querySelector<HTMLElement>('#tabela')!;
  const paginacao = raiz.querySelector<HTMLElement>('#paginacao')!;
  const kpis = raiz.querySelector<HTMLElement>('#kpis')!;

  let timer: number | undefined;
  campo.addEventListener('input', () => {
    window.clearTimeout(timer);
    // Espera parar de digitar: sem isso "cimento" dispara sete consultas, e a
    // última a chegar pode não ser a última pedida.
    timer = window.setTimeout(() => {
      estado.busca = campo.value.trim();
      estado.pagina = 1;
      void carregar();
    }, 250);
  });
  filtro.addEventListener('change', () => {
    estado.filtro = filtro.value;
    estado.pagina = 1;
    void carregar();
  });
  ordem.addEventListener('change', () => {
    // O seletor continua existindo; "Maior saldo" é saldo em ordem invertida.
    estado.ordem = ordem.value === 'saldo_desc' ? 'saldo' : ordem.value;
    estado.direcao =
      ordem.value === 'saldo_desc' || ordem.value.startsWith('ultima_') ? 'desc' : 'asc';
    estado.pagina = 1;
    void carregar();
  });

  async function carregarResumo() {
    try {
      const r = await api.get<Resumo>('/api/estoque/resumo');
      kpis.innerHTML = [
        kpi('Itens ativos', r.itens, ''),
        kpi('Saldo negativo', r.negativos, r.negativos > 0 ? 'alerta' : ''),
        kpi('Zerados', r.zerados, ''),
        kpi('Comprometidos', r.comprometidos, ''),
        kpi('Vencidos', r.vencidos, r.vencidos > 0 ? 'alerta' : ''),
        kpi('Vencendo em 30d', r.vencendo, r.vencendo > 0 ? 'aviso' : ''),
      ].join('');
    } catch {
      kpis.innerHTML = '';
    }
  }

  async function carregar() {
    const meu = ++ultima;
    try {
      const p = new URLSearchParams({
        pagina: String(estado.pagina),
        porPagina: '50',
        filtro: estado.filtro,
        ordem: estado.ordem,
        direcao: estado.direcao,
      });
      if (estado.busca) p.set('busca', estado.busca);
      const r = await api.get<Resposta>(`/api/estoque?${p}`);
      if (meu !== ultima) return; // resposta atrasada não sobrescreve a atual
      desenhar(r);
    } catch (erro) {
      if (meu !== ultima) return;
      tabela.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui carregar o estoque.',
      )}</p>`;
    }
  }

  function desenhar(r: Resposta) {
    if (r.itens.length === 0) {
      tabela.innerHTML = `<p class="vazio">Nada encontrado.</p>`;
      paginacao.innerHTML = '';
      return;
    }

    tabela.innerHTML = `
      <div class="rolagem">
        <table class="tabela" data-ordenacao-propria>
          <thead>
            <tr>
              ${cabecalho('codigo', 'Código')}${cabecalho('nome', 'Nome')}
              ${cabecalho('saldo', 'Físico', 'num')}<th class="num">Compromet.</th><th class="num">Disponível</th>
              <th>Un.</th>${cabecalho('ultima_entrada', 'Última entrada')}${cabecalho('ultima_saida', 'Última saída')}<th>Validade</th><th></th>
            </tr>
          </thead>
          <tbody>${r.itens.map(linha).join('')}</tbody>
        </table>
      </div>`;

    paginacao.innerHTML =
      r.paginas <= 1
        ? `<span>${r.total} item(ns)</span>`
        : `<button class="btn btn-claro" id="ant" ${r.pagina <= 1 ? 'disabled' : ''}>← Anterior</button>
           <span>Página ${r.pagina} de ${r.paginas} · ${r.total} item(ns)</span>
           <button class="btn btn-claro" id="prox" ${r.pagina >= r.paginas ? 'disabled' : ''}>Próxima →</button>`;
    paginacao.querySelector('#ant')?.addEventListener('click', () => {
      estado.pagina--;
      void carregar();
    });
    paginacao.querySelector('#prox')?.addEventListener('click', () => {
      estado.pagina++;
      void carregar();
    });
  }

  function linha(i: LinhaEstoque): string {
    const s = SITUACAO[i.situacao];
    // O comprometido só aparece quando existe. Uma coluna cheia de zeros
    // esconde os poucos casos que importam.
    const comp = i.comprometido > 0 ? `<b>${formatar(i.comprometido)}</b>` : '—';
    return `
      <tr>
        <td class="cod">${escapar(i.codigo)}</td>
        <td>${escapar(i.nome)}${
          i.categoria ? `<span class="sub">${escapar(i.categoria)}</span>` : ''
        }</td>
        <td class="num ${classeSaldo(i.fisico)}">${formatar(i.fisico)}</td>
        <td class="num comp">${comp}</td>
        <td class="num ${classeSaldo(i.disponivel)}"><b>${formatar(i.disponivel)}</b></td>
        <td class="un">${escapar(i.unidade ?? '')}</td>
        <td class="data">${dataHora(i.ultimaEntrada)}</td>
        <td class="data">${dataHora(i.ultimaSaida)}</td>
        <td><span class="${s.classe}">${s.texto}</span>${
          i.validade ? `<span class="sub">${data(i.validade)}</span>` : ''
        }</td>
        <td class="acoes">
          <button class="btn btn-claro" data-abrir="${escapar(i.codigo)}"
                  title="Histórico do item e correção de saldo">📜 Histórico</button>
        </td>
      </tr>`;
  }

  /**
   * UM botão só. Eram dois — 📜 histórico e ⚖️ corrigir — e os dois abriam a
   * mesma ficha, o que fazia a tela oferecer uma escolha que não existia. A
   * correção mora dentro do histórico, que é onde ela pertence: ninguém
   * corrige um saldo sem antes ver de onde ele veio.
   */
  /**
   * O cabeçalho clicável — a tabela é paginada NO SERVIDOR, então o clique
   * refaz a consulta com a coluna e a direção (ordenar só a página visível
   * seria mentir); por isso o `data-ordenacao-propria` que dispensa o
   * ordenador global.
   */
  function cabecalho(chave: string, rotulo: string, classe = ''): string {
    const ativa = estado.ordem === chave;
    return `<th ${classe ? `class="${classe}"` : ''} data-ordem="${chave}"
      ${ativa ? `aria-sort="${estado.direcao === 'asc' ? 'ascending' : 'descending'}"` : ''}>${rotulo}</th>`;
  }

  tabela.addEventListener('click', (e) => {
    const th = (e.target as HTMLElement).closest<HTMLElement>('th[data-ordem]');
    if (th) {
      const chave = th.dataset['ordem']!;
      estado.direcao = estado.ordem === chave && estado.direcao === 'asc' ? 'desc' : 'asc';
      estado.ordem = chave;
      estado.pagina = 1;
      void carregar();
      return;
    }
    const alvo = (e.target as HTMLElement).closest<HTMLElement>('[data-abrir]');
    if (alvo) montarExtrato(raiz, alvo.dataset['abrir']!, voltarParaLista);
  });

  /**
   * Volta à lista redesenhando a tela inteira — e é de propósito que ela
   * RECARREGA: quem acabou de ajustar um saldo precisa ver o número novo na
   * tabela, não o de antes guardado em memória.
   */
  function voltarParaLista(): void {
    montarEstoque(raiz);
  }

  void carregarResumo();
  void carregar();
}

function kpi(rotulo: string, valor: number, tom: string): string {
  return `<div class="kpi ${tom}"><span class="kpi-rotulo">${rotulo}</span><span class="kpi-valor">${valor}</span></div>`;
}

function classeSaldo(n: number): string {
  return n < 0 ? 'negativo' : n === 0 ? 'zero' : '';
}

function formatar(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function data(yyyymmdd: string): string {
  const [a, m, d] = yyyymmdd.split('-');
  return `${d}/${m}/${a}`;
}
